import React from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import OrdersTabs from "./OrdersTabs";
import CreateOrderUploadPage from "../admin/orders/CreateOrderUploadPage";
import CreateOrderReviewPage from "../admin/orders/CreateOrderReviewPage";
import CreateVaDeviceOrderModal from "../admin/orders/CreateVaDeviceOrderModal";
import { useAuth } from "../auth/AuthContext";
import { updateOrdersFilterSearch } from "../../app/orderApi/filterSlice";
import { hasRole } from "../../helpers/roleNames";
import { P } from "../../utils/permissions";
import "./order.css";

const Orders = () => {
  const { setIntialSearch, intialSearch } = useOutletContext() ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const createMode = searchParams.get("create");
  const [deviceDraft, setDeviceDraft] = React.useState(null);
  const dispatch = useDispatch();
  const { roles, hasPermission } = useAuth();
  const filterSearch = useSelector((state) => state.filters.search);
  const orderChannel = String(filterSearch?.channel || "").trim().toLowerCase();
  const canReadAllOrders = hasPermission(P.ORDERS_READ_ALL);
  const vaAdminOnly = hasRole(roles, "va-admin") && !canReadAllOrders;
  const superAdmin = hasRole(roles, "super-admin");

  React.useEffect(() => {
    if (vaAdminOnly && orderChannel !== "va") {
      dispatch(updateOrdersFilterSearch({ channel: "va" }));
      return;
    }
    if (!superAdmin && !vaAdminOnly && orderChannel === "va") {
      dispatch(updateOrdersFilterSearch({ channel: undefined }));
    }
  }, [vaAdminOnly, superAdmin, orderChannel, dispatch]);

  // Restore search box text before child effects run (e.g. returning from order details).
  React.useLayoutEffect(() => {
    const savedText =
      filterSearch && typeof filterSearch === "object"
        ? filterSearch.search || ""
        : typeof filterSearch === "string"
          ? filterSearch
          : "";
    if (savedText) {
      setIntialSearch?.(savedText);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeCreateModal = () => {
    setDeviceDraft(null);
    const params = new URLSearchParams(searchParams);
    params.delete("create");
    params.delete("parseId");
    setSearchParams(params);
  };

  const openCreateModal = (mode) => {
    const params = new URLSearchParams(searchParams);
    params.set("create", mode);
    setSearchParams(params);
  };

  return (
    <>
      <OrdersTabs
        intialSearch={intialSearch ?? ""}
        setIntialSearch={setIntialSearch}
        onOpenCreateUpload={() => openCreateModal("upload")}
        onOpenCreateManual={() => openCreateModal("manual")}
        onOpenCreateDevice={() => openCreateModal("device")}
      />

      {createMode === "upload" && (
        <CreateOrderUploadPage asModal onClose={closeCreateModal} />
      )}
      {createMode === "review" && (
        <CreateOrderReviewPage asModal onClose={closeCreateModal} />
      )}
      {createMode === "manual" && (
        <CreateOrderReviewPage asModal manual onClose={closeCreateModal} />
      )}
      {createMode === "device-review" && deviceDraft && (
        <CreateOrderReviewPage asModal deviceDraft={deviceDraft} onClose={closeCreateModal} />
      )}
      {createMode === "device-review" && !deviceDraft && (
        <CreateVaDeviceOrderModal onClose={closeCreateModal} onContinue={(draft) => {
          setDeviceDraft(draft);
        }} />
      )}
      {createMode === "device" && (
        <CreateVaDeviceOrderModal
          onClose={closeCreateModal}
          onContinue={(draft) => {
            setDeviceDraft(draft);
            openCreateModal("device-review");
          }}
        />
      )}
    </>
  );
};

export default Orders;
